import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  FormHelperText,
  Select,
  Grid,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const departmentSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z.string().max(255).optional(),
  department_code: z.string().max(255).optional(),
  parent_id: z.union([z.number(), z.nan(), z.null()]).optional(),
  descriptions: z.string().optional(),
  active: z.boolean(),
})

type DepartmentFormData = z.infer<typeof departmentSchema>

interface DepartmentFormProps {
  departmentId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

interface DepartmentOption {
  id: number
  name: string
  slug?: string
  department_code?: string
  active?: boolean
  parent_id: number | null
}

export function DepartmentForm({ departmentId, onSuccess, onCancel }: DepartmentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [parentOptions, setParentOptions] = useState<DepartmentOption[]>([])
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!departmentId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      name: '',
      slug: '',
      department_code: '',
      parent_id: null,
      descriptions: '',
      active: true,
    },
  })

  const activeValue = watch('active')

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get<DepartmentOption[]>('/options/departments')
        const list = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? []
        setParentOptions(list)
      } catch (e) {
        showError(e)
      }
    }
    fetch()
  }, [showError])

  useEffect(() => {
    if (isEditMode && departmentId) {
      const load = async () => {
        setIsLoading(true)
        try {
          const res = await apiClient.get(`/organization/departments/${departmentId}`)
          const d = res.data?.data ?? res.data ?? {}
          reset({
            name: d.name ?? '',
            slug: d.slug ?? '',
            department_code: d.department_code ?? '',
            parent_id: d.parent_id ?? null,
            descriptions: d.descriptions ?? '',
            active: d.active === 'Active',
          })
        } catch (e) {
          showError(e)
        } finally {
          setIsLoading(false)
        }
      }
      load()
    }
  }, [departmentId, isEditMode, reset, showError])

  const onSubmit = async (data: DepartmentFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        parent_id: data.parent_id && !Number.isNaN(Number(data.parent_id)) ? Number(data.parent_id) : null,
      }
      if (isEditMode && departmentId) {
        await apiClient.put(`/organization/departments/${departmentId}`, payload)
        showSuccess('Success', 'Department updated successfully')
      } else {
        await apiClient.post('/organization/departments', payload)
        showSuccess('Success', 'Department created successfully')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading department...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. Human Resources, Information Technology"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Slug</FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input
              {...register('slug')}
              placeholder="e.g. human-resources, it"
            />
            <FormHelperText>Leave empty to auto-generate from name.</FormHelperText>
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Department Code</FormLabel>
          <FormControl isInvalid={!!errors.department_code}>
            <Input
              {...register('department_code')}
              placeholder="e.g. HR_001"
            />
            <FormHelperText>Leave empty to auto-generate.</FormHelperText>
            <FormErrorMessage>{errors.department_code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent Department</FormLabel>
          <FormControl isInvalid={!!errors.parent_id}>
            <Select
              {...register('parent_id', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
              placeholder="— None (top-level) —"
            >
              {parentOptions
                .filter((d) => !departmentId || d.id !== departmentId)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </Select>
            <FormErrorMessage>{errors.parent_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Descriptions</FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Switch
              isChecked={activeValue}
              onChange={(e) => setValue('active', e.target.checked)}
            />
            <FormHelperText>When inactive, this department is hidden from selection.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Department'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
